import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  colors,
  getStatusStyle,
  getHorairesAujourdHui,
  isOpenNowFromHoraires,
} from '../designSystem';
import {
  fixerLiteHasOpeningHours,
  fixerLiteOpenNow,
  fixerLiteHoursToday,
} from '../../../annuaire/bookingState';

/**
 * Les deux lignes d'infos communes à la carte du listing et à la tête de
 * fiche (lots 2 et 3, validés le 22/09) : les avis en étoiles, puis une ligne
 * de texte « type · ● Ouvert · horaires ». Pas de pastilles : les services
 * sont les seuls tags.
 */

const Avis = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  b {
    color: ${colors.textDark};
  }
`;

const Etoiles = styled.span`
  color: #f5a623;
  letter-spacing: 1px;

  i {
    font-style: normal;
    color: #dfe5ea;
  }
`;

const Ligne = styled.div`
  line-height: 1.5;

  .sep {
    margin: 0 6px;
    color: #c3ccd4;
  }
`;

const Statut = styled.span`
  font-weight: 600;
  color: ${(p) => p.$couleur};

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 5px;
    border-radius: 4px;
    background: currentColor;
    vertical-align: 1px;
  }
`;

/** « ★★★★☆ 4,2 · 12 avis Google », rien sans note. */
export function AvisEtoiles({ note, nbAvis }) {
  if (note == null || note === '') return null;
  const n = Math.max(0, Math.min(5, Math.round(parseFloat(note))));
  return (
    <Avis>
      <Etoiles aria-hidden="true">
        {'★'.repeat(n)}
        <i>{'★'.repeat(5 - n)}</i>
      </Etoiles>
      <b>{Number(note).toFixed(1).replace('.', ',')}</b>
      {nbAvis != null && <span>· {nbAvis} avis Google</span>}
    </Avis>
  );
}

/** « Magasin vélo · ● Ouvert · 9h-18h30 », rien si tout manque. */
export function LigneAtelier({ typologie, statut, horaires }) {
  // Tableau côté API : le premier libellé suffit (comme `TypologieBadge`).
  const type = Array.isArray(typologie) ? typologie[0] : typologie;
  const morceaux = [
    type && <span key="t">{type}</span>,
    statut && (
      <Statut key="s" $couleur={getStatusStyle(statut).color}>
        {statut}
      </Statut>
    ),
    horaires && <span key="h">{horaires}</span>,
  ].filter(Boolean);
  if (morceaux.length === 0) return null;
  return (
    <Ligne>
      {morceaux.map((el, i) => (
        <React.Fragment key={el.key}>
          {i > 0 && <span className="sep">·</span>}
          {el}
        </React.Fragment>
      ))}
    </Ligne>
  );
}

/**
 * Le statut et les horaires du jour d'un atelier, pour la carte du listing et
 * la tête de fiche. Les horaires sont lus comme dans les infos pratiques : la
 * fiche porte souvent `horaires_formatted` (texte, un jour par ligne) sans
 * `horaires`. Statut et horaires partent du même objet, sinon la ligne
 * pourrait dire « Ouvert » un jour de fermeture.
 *
 * Les deux dépendent de l'heure : calculés après le montage seulement, sinon
 * le serveur (UTC) et le navigateur (Paris) rendraient deux textes différents
 * à l'hydratation. D'ici là, « Ouvert » n'est pas affiché ; un statut qui ne
 * dépend pas de l'heure (« Fermé temporairement »…) l'est tout de suite.
 *
 * `shop` (FIXERLIGHT-068, optionnel) : quand le compte Fixer Lite a un
 * gabarit exploitable (`fixerLiteHasOpeningHours`), statut ET horaires du
 * jour en viennent (`fixer_lite.opening_hours`) plutôt que des horaires
 * Google lus ci-dessous. Le garde n'est PAS `isFixerLiteEnabled(shop)` seul :
 * un compte activé sans gabarit réglé (front avant api, ou compte rattaché
 * par `fixer-lite:create-account --shop=<id>` à une fiche Google existante)
 * doit retomber entièrement sur Google, sous peine d'afficher « Fermé » toute
 * la semaine sur un atelier qui ne l'est pas — pire que le bug d'origine.
 */
export function useStatutDuJour(details, shop) {
  const statutEtab = details.statut_etablissement;
  const { horaires: horairesStructures, horaires_formatted: texte } = details;
  const [duJour, setDuJour] = useState(null);
  const fixerLite = fixerLiteHasOpeningHours(shop);

  useEffect(() => {
    const horaires =
      horairesStructures && horairesStructures.weekdayDescriptions
        ? horairesStructures
        : typeof texte === 'string' && texte
        ? { weekdayDescriptions: texte.split('\n') }
        : null;
    let statut = statutEtab;
    if (statutEtab === 'Ouvert') {
      const ouvert = fixerLite
        ? fixerLiteOpenNow(shop)
        : isOpenNowFromHoraires(horaires);
      if (ouvert === true) statut = 'Ouvert';
      else if (ouvert === false) statut = 'Fermé';
    }
    const horairesDuJour = fixerLite
      ? fixerLiteHoursToday(shop)
      : getHorairesAujourdHui(horaires);
    setDuJour({ statut, horaires: horairesDuJour });
  }, [statutEtab, horairesStructures, texte, fixerLite, shop]);

  if (duJour) return duJour;
  return {
    statut: statutEtab === 'Ouvert' ? null : statutEtab,
    horaires: null,
  };
}
